import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { ParquetInlineSettings } from 'app/model/dataflow/dataset-settings/parquet-inline-settings';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowAdapterUtils } from '../adapter.util';
import { DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DataFlowExpressionUtils } from '../expression.util';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(ParquetInlineSettings)
})
export class ParquetFormatAdapter extends FileBasedSettingsAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        const compressionCodec = getAttributeValueFromDataset(dataset, 'compressionCodec');
        if (compressionCodec) {
            props.push(`compressionCodec: '${compressionCodec}'`);
        }
    }

    protected async toSourceDSL(parquetSettings: ParquetInlineSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(parquetSettings)));
        if (parquetSettings.compressionType) {
            props.push(`compressionCodec: '${parquetSettings.compressionType}'`);
        }
        return props;
    }

    protected async toSinkDSL(parquetSettings: ParquetInlineSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSinkDSL(parquetSettings)));
        if (parquetSettings.compressionType) {
            props.push(`compressionCodec: '${parquetSettings.compressionType}'`);
        }
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<ParquetInlineSettings> {
        const parquetSettings = (await super.toSourceModel(owner, configs, handledConfigs)) as ParquetInlineSettings;
        if (configs.has('compressionCodec')) {
            parquetSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionCodec')
            );
            handledConfigs.push('compressionCodec');
        }
        return parquetSettings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<ParquetInlineSettings> {
        const parquetSettings = (await super.toSinkModel(owner, configs, handledConfigs)) as ParquetInlineSettings;
        if (configs.has('compressionCodec')) {
            parquetSettings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionCodec')
            );
            handledConfigs.push('compressionCodec');
        }
        return parquetSettings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): ParquetInlineSettings {
        return Owned.create<ParquetInlineSettings>(ParquetInlineSettings.MODEL, owner);
    }
}
